import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bug, Lightbulb, MessageSquare, Megaphone, ChevronUp, Info } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export default function Footer() {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const currentYear = new Date().getFullYear();

  const links = [
    { icon: <Bug className="h-4 w-4" />, label: 'Report Bug', path: '/bug-report' },
    { icon: <Lightbulb className="h-4 w-4" />, label: 'Feature Request', path: '/feature-request' },
    { icon: <MessageSquare className="h-4 w-4" />, label: 'Feedback', path: '/feedback' },
    { icon: <Megaphone className="h-4 w-4" />, label: 'Advertise', path: '/advertise' },
    { icon: <Info className="h-4 w-4" />, label: 'About Us', path: '/about' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100;
      setIsVisible(scrolledToBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <footer
      className={`
        fixed bottom-0 left-0 right-0 transition-all duration-300 transform
        ${isVisible || isExpanded ? 'translate-y-0' : 'translate-y-full'}
        ${theme === 'dark' ? 'bg-gray-900/95' : 'bg-white/95'}
        backdrop-blur-sm border-t
        ${theme === 'dark' ? 'border-gray-800' : 'border-gray-100'}
      `}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Floating indicator */}
      <div
        className={`
          absolute -top-8 left-1/2 -translate-x-1/2
          px-3 py-1 rounded-t-lg cursor-pointer
          transition-colors duration-200
          ${theme === 'dark'
            ? 'bg-gray-900/95 text-gray-400 hover:text-gray-300'
            : 'bg-white/95 text-gray-500 hover:text-gray-700'
          }
          ${theme === 'dark' ? 'border-gray-800' : 'border-gray-100'}
          border-t border-l border-r backdrop-blur-sm
        `}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ChevronUp className={`h-4 w-4 transition-transform duration-200 ${
          isExpanded ? 'rotate-180' : ''
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`
          py-3 flex items-center justify-between gap-4
          transition-all duration-300
          ${isExpanded ? 'opacity-100' : 'opacity-0'}
        `}>
          {/* Logo and Copyright */}
          <div className="flex items-center space-x-2">
            <img 
              src="/images/group-icon.png" 
              alt="Gocity Group Logo" 
              className="h-5 w-9"
            />
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              © {currentYear} Gocity Groups
            </span>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            {links.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  group flex items-center space-x-2 text-sm transition-colors
                  ${theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-500 hover:text-gray-900'
                  }
                `}
              >
                <span className={`
                  transition-transform duration-200
                  group-hover:-translate-y-1
                `}>
                  {link.icon}
                </span>
                <span className="hidden sm:inline">{link.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}